import {
  BaseTransactionModel,
  BaseTransactionSkuModel,
} from './base-transaction-model';

import { merge, reduce, round, some, find } from 'lodash';

/**
 * The payments made during a sale
 */
export class SalePaymentsModel {
  cash: number;
  consignment: number;
  couponin: number;
  creditcard: number;
  creditreceipt: number;
  pin: number;
  storecredit: number;
  webshop: number;
  withdrawel: number;
}

/**
 * The coupon created during a sale
 */
export class SaleCouponModel {
  id: string;
  type: string;
  value: number;
}

/**
 * The optional customer added to a sale
 */
export class SaleCustomerModel {
  custnum: string;
  email: string;
  firstname: string;
  id: string;
  lastname: string;
  loyaltynr: string;
  middlename: string;
  points: number;
  telephone: string;
  title: string;
}

/**
 * The VAT calculated during a sale
 */
export class SaleVatModel {
  highamount: number;
  hightotal: number;
  highvalue: number;
  lowamount: number;
  lowtotal: number;
  lowvalue: number;
  zeroamount: number;
  zerototal: number;
  zerovalue: number;

  // not documented in spynl.app, but used in sales-calculations
  lowbruto: number;
  zerobruto: number;
  highbruto: number;
}

/**
 * The sale transaction model
 */
export class SaleModel extends BaseTransactionModel {
  change: number;
  coupon: SaleCouponModel[];
  couponDiscount: number;
  customer: SaleCustomerModel;
  difference: number;
  discountreason: string;
  loyaltyPoints: number;
  payments: SalePaymentsModel;
  pinError: string;
  pinInfo: string;
  totalAmount: number;
  totalCoupon: number;
  totalDiscount: number;
  totalDiscountCoupon: number;
  totalNumber: number;
  totalReturn: number;
  totalPaid: number;
  totalStoreCreditPaid: number;
  vat: SaleVatModel;
  withdrawelreason: string;

  // not documented in spynl.app, but used in sales-calculations
  articlesHaveDiscount: boolean;
  changeAbsolute: number;
  created?: {
    date: string;
  };
  diff: number;
  discountableAmount: number;
  finalTotalDiscount: number;
  line: any;
  modified?: { date: string };
  overallReceiptDiscount: number;
  paymentsList: object[];
  status: string;
  total: number;
  totalCustomerName: string;
  fiscal_receipt_nr?: string;
  fiscal_shift_nr?: string;
  fiscal_printer_id?: string;
  fiscal_date?: string;
  return_original_nr?: string;

  constructor(originalValue?: PartialSaleModel) {
    super();
    merge(this, originalValue);
  }

  getUndiscountedTotal(): number {
    if (this.overallReceiptDiscount > 0) {
      return 0;
    }

    // TODO POS-1840 use (totalDiscountCoupon > 0) once `totalDiscountCoupon`
    // is set correctly
    if (
      some(this.receipt, (line) => {
        return line?.type === ' ' && line?.value > 0;
      })
    ) {
      return 0;
    }

    return reduce(
      this.receipt,
      (sum, line) => {
        // Redeemed cashback coupons are excepted from givePointsOnDiscount=false, but we
        // do need to subtract their value when generating a new cashback.
        if (line.type === 'C') {
          return sum - line.value;
        }

        // Redeemed credit store coupons are excepted from givePointsOnDiscount=false, but we
        // do need to subtract their value when generating a new cashback.
        if (line.type === 'T') {
          return sum - line.lineTotal;
        }

        // Skip discounted barcodes.
        if (line.price !== line.nettPrice) {
          return sum;
        }

        // Skip receipt lines that aren't barcodes.
        if (line.category !== 'barcode') {
          return sum;
        }

        if (angular.isNumber(line.price) && angular.isNumber(line.qty)) {
          return sum + line.price * line.qty;
        }
      },
      0,
    );
  }

  /**
   * Summarize all payment
   * @return {number} summed up payment
   */
  getTotalPaid() {
    function defaultValue(input) {
      return !input ? 0 : input;
    }

    // TODO consider replacing by reading totalPaid
    return round(
      defaultValue(this.totalAmount) -
        defaultValue(this.overallReceiptDiscount) -
        defaultValue(this.totalDiscountCoupon) -
        defaultValue(this.totalCoupon) -
        defaultValue(this.totalStoreCreditPaid),
      2,
    );
  }

  /**
   * Runs basic sanity check on the transaction
   * @return true when all fine, false otherwise
   */
  hasValidTotals(): boolean {
    const sumOfLineTotals = reduce(
      this.receipt,
      (sum, line) => {
        return sum + line.lineTotal;
      },
      0,
    );

    if (this.totalAmount === sumOfLineTotals) {
      return true;
    }

    return false;
  }

  /**
   * Update the object with PIN payment
   * @param amount we want to set on the transaction as a PIN payment
   */
  setPinPayment(amount: number) {
    this.payments.pin = amount;
    this.change += amount;
  }

  /**
   * Calculate what should be still paid on the transaction
   * @return pending amount
   */
  getPendingPayment(): Number {
    return round(-this.change - this.difference, 2);
  }

  /**
   * Checks if sale has coupons
   * @return true if has, false otherwise
   */
  hasCoupons(): boolean {
    if (!angular.isArray(this.receipt) || this.receipt.length === 0) {
      return false;
    }

    const coupon = find(this.receipt, (line) => {
      switch (line.type) {
        case 'A':
        case 'KA':
          // voucher
          return true;
        case 'C':
        case 'KC':
          // cashback_coupon
          return true;
        case ' ':
        case 'K':
          // discount_coupon
          return true;
        case 'I':
        case 'KI':
          // discount_sold
          return true;
        case 'U':
        case 'KU':
          // redeemed_coupon
          return true;
        case 'T':
        case 'KT':
          // credit_receipt
          return true;
        case 'O':
          // credit_receipt
          return false;
      }

      return false;
    });

    return !!coupon;
  }

  /**
   * Checks if sale can has consignment added
   * @return current state
   */
  allowsConsignment(): boolean {
    if (this.hasCoupons()) {
      return false;
    }

    if (this.couponDiscount > 0) {
      return false;
    }

    if (this.overallReceiptDiscount > 0) {
      return false;
    }

    return true;
  }
}

export type PartialSaleModel = Partial<
  Omit<SaleModel, 'payments' | 'vat' | 'customer' | 'receipt'>
> & {
  customer?: Partial<SaleCustomerModel>;
  payments?: Partial<SalePaymentsModel>;
  vat?: Partial<SaleVatModel>;
  receipt?: Partial<BaseTransactionSkuModel>[];
};

// Hack! It makes sure that in the test scripts & test are using the same
// reference to the class
export const SaleModelProvider = {
  provide: 'SaleModel',
  useValue: SaleModel,
};
